'use client';
import { PlayIcon } from '@heroicons/react/24/solid';
import { clsx } from 'clsx';
import { useTranslations } from 'next-intl';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { HeroIcon, Image, Typography } from '@/components/atoms';
import { LinkButton } from '@/components/molecules/LinkButton';
import { VideoPlayer } from '@/components/organisms/VideoPlayer';
import { cn } from '@/lib/utils/tailwind';

import styles from './CTAPreviewBanner.module.css';
import { CTAPreviewBannerProps } from './types';

import type {
  HeaderPreviewClip,
  LinkedContent,
  LinkedPage,
} from '@/lib/api/types';

const options: IntersectionObserverInit = {
  root: null,
  threshold: 0.5,
};

export const CTAPreviewBanner = ({
  title,
  description,
  previewClips,
  primaryButtonText,
  linkedContent,
  loop = false,
}: CTAPreviewBannerProps) => {
  const [show, setShow] = useState<'video' | 'poster'>('video');
  const [isLandscape, setIsLandscape] = useState<boolean | undefined>();

  const videoRef = useRef<HTMLVideoElement>(null);
  const bannerRef = useRef<HTMLDivElement>(null);
  const responsiveRef = useRef<HTMLDivElement>(null);

  const t = useTranslations();
  const isPage = (content: LinkedContent | LinkedPage): content is LinkedPage =>
    content.content_type === 'page';

  const generateURL = (content: LinkedContent | LinkedPage) => {
    if (isPage(content)) {
      return content.home_page ? '/' : '/' + content.slug;
    }
    return `/${content.content_type}/${content.id}`;
  };

  const handleOnResize: IntersectionObserverCallback = entries => {
    if (entries.length === 0) return;
    const [entry] = entries;
    setIsLandscape(entry.boundingClientRect.width !== 0);
  };

  const resizeRef = useRef<IntersectionObserver>(
    new IntersectionObserver(handleOnResize, { threshold: 1 }),
  );

  const resolvePreviewClipOrientation = useCallback(():
    | HeaderPreviewClip
    | undefined => {
    if (isLandscape === undefined) return undefined;

    const { portrait, landscape } = previewClips;
    return isLandscape ? (landscape ?? portrait) : (portrait ?? landscape);
  }, [isLandscape, previewClips]);

  const previewClip = resolvePreviewClipOrientation();

  const handleOnIntersect: IntersectionObserverCallback = entries => {
    if (entries.length === 0) return;
    const [banner] = entries;

    if (loop && banner.isIntersecting) {
      videoRef.current?.play();
    } else if (loop && !banner.isIntersecting) {
      videoRef.current?.pause();
    } else if (banner.isIntersecting) {
      setShow('video');
    }
  };

  const intersectionRef = useRef<IntersectionObserver>(
    new IntersectionObserver(handleOnIntersect, options),
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const _intersectionRef = intersectionRef.current;
    const _resizeRef = resizeRef.current;

    if (bannerRef.current) {
      _intersectionRef.observe(bannerRef.current);
    }

    if (responsiveRef.current) {
      _resizeRef.observe(responsiveRef.current);
    }

    return () => {
      _intersectionRef.disconnect();
      _resizeRef.disconnect();
    };
  }, []);

  useEffect(() => {
    if (show !== 'video' || !videoRef.current || isLandscape === undefined) {
      return;
    }

    const previewClip = resolvePreviewClipOrientation();

    if (!previewClip || !previewClip.playback_reference) {
      return;
    }

    switch (previewClip.video_provider) {
      case 'mux':
        if (previewClip.security?.playback_token) {
          videoRef.current.setAttribute(
            'playback-token',
            previewClip.security.playback_token,
          );
        }

        videoRef.current.setAttribute(
          'playback-id',
          previewClip.playback_reference,
        );
        break;
      default:
        videoRef.current.setAttribute('src', previewClip.playback_reference);
        break;
    }
  }, [show, isLandscape, resolvePreviewClipOrientation]);

  const handleOnVideoPlayerEnded = () => setShow('poster');

  let contentTypeTranslated = '';
  if (
    linkedContent &&
    linkedContent !== undefined &&
    linkedContent.content_type !== 'page'
  ) {
    contentTypeTranslated = t(
      `CONTENT_TYPE_${linkedContent.content_type.toUpperCase()}`,
    );
  }

  return (
    <div
      ref={bannerRef}
      className="w-full aspect-[4/5] sm:aspect-video sm:max-h-[85vh] relative"
    >
      <div ref={responsiveRef} className="hidden sm:flex" />
      {/* TODO - Future. we should add the tenentId prop populated with—
      siteConfigData.configuration.tenant_id  */}
      {show === 'video' && previewClip?.video_provider && (
        <VideoPlayer
          ref={videoRef}
          type={previewClip.video_provider}
          mode="preview"
          title={title || ''}
          videoUrl={''}
          onEnded={handleOnVideoPlayerEnded}
          muted
          autoPlay
          loop={loop}
        />
      )}
      <div
        className={clsx({
          'opacity-100': show === 'poster',
          'absolute top-0 left-0 right-0 bottom-0 transition-opacity duration-1000 opacity-0': true,
        })}
      >
        <Image
          alt=""
          src={`${previewClip?.thumbnail?.imageRootUrl}/xlarge${previewClip?.thumbnail?.imagePath}`}
          fill
          className="object-cover"
        />
      </div>
      <div className={`absolute top-0 right-0 bottom-0 left-0 bg-cover`}>
        <div className="size-full text-border-neutral-softer-3 bg-gradient-hero">
          <div
            className={cn(
              styles.content,
              '[align-items:var(--hero-content-item-alignment)] [text-align:var(--hero-content-text-alignment)] absolute max-w-[630px] text-neutral-0 flex flex-col gap-6',
            )}
          >
            <div>
              <div>
                {contentTypeTranslated !== '' && (
                  <Typography.Text
                    variant={'body'}
                    className={cn([
                      'hero-text-display',
                      'px-2',
                      'py-1',
                      'w-fit',
                      'hero-text-style',
                      'bg-(--hero-text-background)',
                      'text-shadow-(--hero-text-shadow)',
                      'text-(--hero-text-color)',
                      'text-(length:--text-body-md)',
                      'font-(--font-weight-body-md-bold)',
                    ])}
                  >
                    {contentTypeTranslated}
                  </Typography.Text>
                )}
              </div>
              {title && (
                <div>
                  <Typography.Heading
                    variant="h1"
                    className={cn([
                      'px-2',
                      'leading-none!',
                      'hero-text-display',
                      'hero-box-decoration',
                      'bg-(--hero-text-background)',
                      'text-(--hero-text-color)',
                      'text-shadow-(--hero-text-shadow)',
                      'max-w-[25ch]',
                      'text-balance',
                      'text-(length:--text-h6)',
                      'md:text-(length:--text-h5)',
                      'font-(--font-weight-h6)',
                    ])}
                  >
                    {title}
                  </Typography.Heading>
                </div>
              )}
              {description && (
                <div className="bg-(--hero-text-background) py-1 px-2 hidden md:inline-flex">
                  <Typography.Text
                    variant="paragraph"
                    className={cn([
                      'hero-text-display',
                      'hero-box-decoration',
                      'hero-text-style',
                      'max-w-[36ch]',
                      'text-shadow-(--hero-text-shadow)',
                      'text-(--hero-text-color)',
                      'line-clamp-2!',
                      'sm:line-clamp-3!',
                      'md:line-clamp-none!',
                    ])}
                  >
                    {description}
                  </Typography.Text>
                </div>
              )}
            </div>
            {linkedContent && (
              <div className="mt-(--horizontal-spacing-6)">
                <LinkButton href={generateURL(linkedContent)}>
                  <HeroIcon icon={PlayIcon} />
                  <span>{primaryButtonText}</span>
                </LinkButton>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
